import React, { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { 
  MessageCircle, 
  Send, 
  Bot, 
  User, 
  Loader2,
  Crown,
  Sparkles
} from 'lucide-react';

interface ChatMessage {
  id: string;
  message: string;
  response: string;
  timestamp: string;
}

export const VirtualTeacherChat: React.FC = () => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const isMembre = profile?.subscription_type === 'membre' || profile?.subscription_type === 'premium';
  const isTeacher = profile?.role === 'teacher';

  const sendMessage = async () => {
    if (!newMessage.trim()) {
      toast({
        title: "❌ Message vide",
        description: "Veuillez saisir une question",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    console.log('🤖 Sending message:', newMessage);
    
    try {
      // Call the working edge function
      const { data, error } = await supabase.functions.invoke('final_chat_ai_2025_12_10_13_30', {
        body: { message: newMessage }
      });
      
      console.log('🤖 Response data:', data);
      console.log('🤖 Response error:', error);

      if (error) {
        console.error('❌ Supabase function error:', error);
        throw new Error(`Erreur Supabase: ${error.message}`);
      }

      if (data && data.success) {
        // Add conversation to local state
        const newChat: ChatMessage = {
          id: Date.now().toString(),
          message: newMessage,
          response: data.response,
          timestamp: new Date().toLocaleString('fr-FR')
        };

        setMessages(prev => [...prev, newChat]);
        setNewMessage('');

        toast({
          title: "✅ Réponse reçue !",
          description: "Le professeur IA a répondu à votre question",
        });
      } else {
        throw new Error(data?.error || 'Réponse invalide du serveur');
      }
    } catch (error: any) {
      console.error('❌ Chat error:', error);
      toast({
        title: "❌ Erreur de chat",
        description: `Problème: ${error.message}. Vérifiez votre quota OpenAI.`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ACCÈS POUR TOUS LES PREMIUM ET ENSEIGNANTS
  if (!isMembre && !isTeacher) {
    return (
      <div className="text-center py-12">
        <Crown className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-secondary mb-4">
          Professeur Virtuel Membre
        </h2>
        <p className="text-muted-foreground mb-6">
          Cette fonctionnalité est réservée aux membres Membre
        </p>
        <Badge className="sprintbac-gradient text-white">
          <Crown className="mr-2 h-4 w-4" />
          Accès Membre Requis
        </Badge>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="text-center">
        <div className="flex items-center justify-center gap-3 mb-4">
          <Bot className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold text-secondary">
            Professeur Virtuel IA
          </h1>
          <Sparkles className="h-6 w-6 text-yellow-500" />
        </div>
        <p className="text-muted-foreground">
          Posez vos questions académiques et recevez des réponses personnalisées
        </p>
      </div>

      {/* Status */}
      <Card className="border-2 border-green-200 bg-green-50">
        <CardContent className="pt-6">
          <div className="text-center">
            <h3 className="font-semibold text-green-800 mb-2">✅ Chat IA Fonctionnel</h3>
            <p className="text-sm text-green-700">
              Statut: {isMembre ? '👑 Membre' : isTeacher ? '👨‍🏫 Enseignant' : '❌ Accès refusé'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Chat Interface */}
      <Card className="border-0 sprintbac-shadow">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            Conversation avec votre professeur IA
          </CardTitle>
          <CardDescription>
            Assistance académique powered by ChatGPT
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Chat Messages */}
          <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
            {messages.length === 0 ? (
              <div className="text-center py-8">
                <Bot className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  Commencez une conversation avec votre professeur virtuel !
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Posez des questions sur les mathématiques, sciences, informatique ou langues
                </p>
              </div>
            ) : (
              messages.map((msg) => (
                <div key={msg.id} className="space-y-3">
                  {/* User Message */}
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 bg-blue-50 p-3 rounded-lg">
                      <p className="text-sm font-medium text-secondary mb-1">
                        Vous ({msg.timestamp})
                      </p>
                      <p className="text-sm">{msg.message}</p>
                    </div>
                  </div>

                  {/* AI Response */}
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Bot className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1 bg-gradient-to-r from-yellow-50 to-orange-50 p-3 rounded-lg border border-yellow-200">
                      <p className="text-sm font-medium text-secondary mb-1 flex items-center gap-2">
                        Professeur IA
                        <Sparkles className="h-3 w-3 text-yellow-500" />
                      </p>
                      <div className="text-sm whitespace-pre-wrap">{msg.response}</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Message Input */}
          <div className="space-y-4">
            <Textarea
              placeholder="Posez votre question académique ici... (Appuyez sur Entrée pour envoyer)"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              rows={3}
              className="resize-none"
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                💡 Conseil : Soyez précis dans vos questions pour obtenir de meilleures réponses
              </p>
              <Button
                onClick={sendMessage}
                disabled={loading || !newMessage.trim()}
                className="sprintbac-gradient hover:opacity-90"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Envoi...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Envoyer
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Features Info */}
      <Card className="border-0 bg-gradient-to-r from-blue-50 to-yellow-50">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div>
              <Bot className="h-8 w-8 text-primary mx-auto mb-2" />
              <h4 className="font-semibold text-secondary mb-1">IA Avancée</h4>
              <p className="text-sm text-muted-foreground">
                Powered by ChatGPT pour des réponses précises
              </p>
            </div>
            <div>
              <MessageCircle className="h-8 w-8 text-primary mx-auto mb-2" />
              <h4 className="font-semibold text-secondary mb-1">7j/7</h4>
              <p className="text-sm text-muted-foreground">
                Assistance disponible tous les jours
              </p>
            </div>
            <div>
              <Sparkles className="h-8 w-8 text-primary mx-auto mb-2" />
              <h4 className="font-semibold text-secondary mb-1">Personnalisé</h4>
              <p className="text-sm text-muted-foreground">
                Réponses adaptées à votre niveau
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};